import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Play, Pause, Volume2, VolumeX, Sparkles, Users, Zap, Brain, MessageSquare, BarChart3, Clock, Globe, Shield } from 'lucide-react';
import { useThemeStore } from '../../store/theme';

// Slide data for Demo Video
const demoSlides = [
  {
    id: 1,
    title: 'Meet Your AI Team',
    subtitle: '15 Specialized AI Employees',
    description: 'Each with unique skills to handle any business task',
    icon: Users,
    gradient: 'from-purple-500 to-indigo-500',
    features: ['Marketing', 'Sales', 'Support', 'Operations'],
  },
  {
    id: 2,
    title: 'Natural Conversations',
    subtitle: 'Chat Like With Real Experts',
    description: 'AI that understands context and delivers human-like responses',
    icon: MessageSquare,
    gradient: 'from-indigo-500 to-cyan-500',
    features: ['Context-aware', 'Memory', 'Streaming', 'Multi-language'],
  },
  {
    id: 3,
    title: 'Powerful Automation',
    subtitle: 'Set It and Forget It',
    description: 'Schedule tasks and let AI handle them automatically',
    icon: Zap,
    gradient: 'from-cyan-500 to-green-500',
    features: ['Scheduling', 'Workflows', 'Triggers', 'Integrations'],
  },
  {
    id: 4,
    title: 'Train Your AI Brain',
    subtitle: 'Custom Knowledge Base',
    description: 'Upload docs, websites, and instructions for personalized AI',
    icon: Brain,
    gradient: 'from-green-500 to-yellow-500',
    features: ['Documents', 'Websites', 'Instructions', 'Learning'],
  },
  {
    id: 5,
    title: 'Start Free Today',
    subtitle: 'No Credit Card Required',
    description: 'Join 50,000+ businesses transforming their workflows',
    icon: Sparkles,
    gradient: 'from-yellow-500 to-purple-500',
    features: ['Free trial', '15 AI employees', '24/7 support', 'Cancel anytime'],
  },
];

// Slide data for Customer Stories
const customerSlides = [
  {
    id: 1,
    name: 'Sarah Chen',
    role: 'Marketing Director',
    company: 'TechFlow Inc',
    quote: 'SweetPilot transformed how we handle content creation. Our engagement increased by 340% in just 3 months.',
    metric: '340%',
    metricLabel: 'Engagement Increase',
    gradient: 'from-purple-500 to-pink-500',
  },
  {
    id: 2,
    name: 'Michael Torres',
    role: 'Founder & CEO',
    company: 'ScaleUp Studio',
    quote: 'I replaced 3 freelancers with SweetPilot AI employees. The quality is better and I save over $4,000 per month.',
    metric: '$4,000+',
    metricLabel: 'Monthly Savings',
    gradient: 'from-cyan-500 to-blue-500',
  },
  {
    id: 3,
    name: 'Emma Thompson',
    role: 'Customer Success Lead',
    company: 'CloudServe',
    quote: 'Nova handles 80% of our support tickets automatically. Response time dropped from 4 hours to 5 minutes.',
    metric: '80%',
    metricLabel: 'Tickets Automated',
    gradient: 'from-green-500 to-cyan-500',
  },
  {
    id: 4,
    name: 'David Park',
    role: 'eCommerce Manager',
    company: 'StyleHouse',
    quote: 'Flux manages our product listings across 5 platforms. What took a full day now happens in under an hour.',
    metric: '95%',
    metricLabel: 'Time Saved',
    gradient: 'from-orange-500 to-red-500',
  },
  {
    id: 5,
    name: 'Join Our Success Stories',
    role: 'Your Company',
    company: 'Could Be Next',
    quote: 'Start your free trial today and see the difference AI employees can make for your business.',
    metric: 'Free',
    metricLabel: 'Start Today',
    gradient: 'from-purple-500 to-cyan-500',
  },
];

interface PromoVideoProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'demo' | 'stories';
}

export default function PromoVideo({ isOpen, onClose, type }: PromoVideoProps) {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const [progress, setProgress] = useState(0);

  const slides = type === 'demo' ? demoSlides : customerSlides;
  const slideDuration = 5000; // 5 seconds per slide

  // Auto-advance slides
  useEffect(() => {
    if (!isOpen || !isPlaying) return;

    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          setCurrentSlide(current => (current + 1) % slides.length);
          return 0;
        }
        return prev + (100 / (slideDuration / 50));
      });
    }, 50);

    return () => clearInterval(progressInterval);
  }, [isOpen, isPlaying, slides.length, slideDuration]);

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setCurrentSlide(0);
      setProgress(0);
      setIsPlaying(true);
    }
  }, [isOpen]);

  // Prevent body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
    setProgress(0);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-md"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="relative w-full max-w-4xl bg-dark-900 rounded-2xl overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-20 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Video content area */}
            <div className="aspect-video relative overflow-hidden bg-gradient-to-br from-dark-800 to-dark-900">
              {/* Background effects */}
              <div className="absolute inset-0">
                <motion.div
                  className={`absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-3xl opacity-30 bg-gradient-to-r ${slides[currentSlide].gradient}`}
                  animate={{ scale: [1, 1.2, 1], rotate: [0, 90, 0] }}
                  transition={{ duration: 10, repeat: Infinity }}
                />
                <motion.div
                  className={`absolute bottom-1/4 right-1/4 w-64 h-64 rounded-full blur-3xl opacity-20 bg-gradient-to-r ${slides[currentSlide].gradient}`}
                  animate={{ scale: [1.2, 1, 1.2], rotate: [90, 0, 90] }}
                  transition={{ duration: 8, repeat: Infinity }}
                />
              </div>

              {/* Slide content */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentSlide}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5 }}
                  className="absolute inset-0 flex items-center justify-center p-8"
                >
                  {type === 'demo' ? (
                    // Demo slide layout
                    <div className="text-center max-w-2xl">
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.2, type: 'spring' }}
                        className={`w-24 h-24 mx-auto mb-6 rounded-2xl bg-gradient-to-br ${slides[currentSlide].gradient} flex items-center justify-center shadow-2xl`}
                      >
                        {(() => {
                          const Icon = (slides[currentSlide] as typeof demoSlides[0]).icon;
                          return <Icon className="w-12 h-12 text-white" />;
                        })()}
                      </motion.div>
                      <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                        className="text-4xl md:text-5xl font-bold text-white mb-2"
                      >
                        {(slides[currentSlide] as typeof demoSlides[0]).title}
                      </motion.h2>
                      <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                        className={`text-xl bg-gradient-to-r ${slides[currentSlide].gradient} bg-clip-text text-transparent font-semibold mb-4`}
                      >
                        {(slides[currentSlide] as typeof demoSlides[0]).subtitle}
                      </motion.p>
                      <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5 }}
                        className="text-gray-400 mb-6"
                      >
                        {(slides[currentSlide] as typeof demoSlides[0]).description}
                      </motion.p>
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.6 }}
                        className="flex flex-wrap justify-center gap-3"
                      >
                        {(slides[currentSlide] as typeof demoSlides[0]).features.map((feature, i) => (
                          <motion.span
                            key={feature}
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.7 + i * 0.1 }}
                            className="px-4 py-2 rounded-full bg-white/10 text-white text-sm"
                          >
                            {feature}
                          </motion.span>
                        ))}
                      </motion.div>
                    </div>
                  ) : (
                    // Customer stories slide layout
                    <div className="text-center max-w-2xl">
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.2, type: 'spring' }}
                        className={`w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br ${slides[currentSlide].gradient} flex items-center justify-center text-3xl font-bold text-white shadow-2xl`}
                      >
                        {(slides[currentSlide] as typeof customerSlides[0]).name.charAt(0)}
                      </motion.div>
                      <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                        className="text-2xl md:text-3xl text-white mb-6 italic leading-relaxed"
                      >
                        "{(slides[currentSlide] as typeof customerSlides[0]).quote}"
                      </motion.p>
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                        className="mb-4"
                      >
                        <div className="text-white font-semibold text-lg">{(slides[currentSlide] as typeof customerSlides[0]).name}</div>
                        <div className="text-gray-400">
                          {(slides[currentSlide] as typeof customerSlides[0]).role} at {(slides[currentSlide] as typeof customerSlides[0]).company}
                        </div>
                      </motion.div>
                      <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.5, type: 'spring' }}
                        className={`inline-block px-6 py-3 rounded-xl bg-gradient-to-r ${slides[currentSlide].gradient}`}
                      >
                        <div className="text-3xl font-bold text-white">{(slides[currentSlide] as typeof customerSlides[0]).metric}</div>
                        <div className="text-white/80 text-sm">{(slides[currentSlide] as typeof customerSlides[0]).metricLabel}</div>
                      </motion.div>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>

              {/* Progress indicators */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                {slides.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => goToSlide(i)}
                    className="relative h-1.5 rounded-full overflow-hidden bg-white/20 cursor-pointer"
                    style={{ width: i === currentSlide ? 48 : 24 }}
                  >
                    {i === currentSlide && (
                      <motion.div
                        className="absolute inset-y-0 left-0 bg-white"
                        style={{ width: `${progress}%` }}
                      />
                    )}
                    {i < currentSlide && (
                      <div className="absolute inset-0 bg-white" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Controls bar */}
            <div className="flex items-center justify-between p-4 bg-dark-800/50 border-t border-white/5">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setIsPlaying(!isPlaying)}
                  className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
                >
                  {isPlaying ? (
                    <Pause className="w-5 h-5 text-white" />
                  ) : (
                    <Play className="w-5 h-5 text-white" />
                  )}
                </button>
                <button
                  onClick={() => setIsMuted(!isMuted)}
                  className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
                >
                  {isMuted ? (
                    <VolumeX className="w-5 h-5 text-white" />
                  ) : (
                    <Volume2 className="w-5 h-5 text-white" />
                  )}
                </button>
              </div>
              <div className="text-sm text-gray-400">
                {currentSlide + 1} / {slides.length}
              </div>
              <a
                href="/register"
                className="px-4 py-2 rounded-lg bg-gradient-to-r from-purple-500 to-cyan-500 text-white text-sm font-medium hover:shadow-lg hover:shadow-purple-500/25 transition-all"
              >
                Get Started Free
              </a>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
